import { RheologyDataDto } from 'providers/api';
import { calculateViscosityfromConcentrationByMass, calculateYieldfromConcentrationByMass, WATER_VISCOSITY_25C } from 'utils/backfill';
import { firstSelectableRheology } from 'utils/rheology';
import { RecipeState } from '../../MineModel/types';
import PCRoute from '../../MineModel/Route/PCRoute';
import type { Model } from '../../hooks/useModelState';
import { returnShortestRoute } from '../../MineModel/Route/utils';

export const resolveRheology = (model: Model, recipeState?: RecipeState): RheologyDataDto | undefined => {
  if (!model.rheology || model.rheology.length === 0) return undefined;
  const id = recipeState?.rheologyDataSetId;
  if (id) {
    const match = model.rheology.find((r) => r.rheologyDataSetId === id);
    if (match) return match;
  }
  return firstSelectableRheology(model.rheology) ?? model.rheology[0];
};

export function frictionFromRheology(
  yieldStress: number,
  viscosity: number,
  pipeInnerDiameter: number,
  velocity: number,
): number | undefined {
  if (yieldStress <= 0 || viscosity <= 0 || pipeInnerDiameter <= 0 || velocity <= 0) return undefined;

  const n = 1;
  let low = yieldStress;
  let high = 100000;
  let t0 = yieldStress;
  const lhs = (8 * velocity) / pipeInnerDiameter;

  for (let counter = 0; counter < 1000; counter += 1) {
    t0 = (low + high) / 2;
    const diff = t0 - yieldStress;
    // eslint-disable-next-line no-mixed-operators
    const rhs1 = (4 * n) / (t0 ** 3) * ((1 / viscosity) ** (1 / n)) * (diff ** ((n + 1) / n));
    // eslint-disable-next-line max-len
    const rhs2 = (diff ** 2) / (3 * n + 1) + (2 * yieldStress * diff) / (2 * n + 1) + (yieldStress ** 2) / (n + 1);
    const rhs = rhs1 * rhs2;

    if (rhs > lhs) high = t0;
    else low = t0;

    if (Math.abs(lhs - rhs) < 0.0001) break;
  }

  const frictionLoss = (t0 * 4) / (pipeInnerDiameter * 1000);
  return Number.isFinite(frictionLoss) ? frictionLoss : undefined;
}

function homogeneousNonNewtonian(
  rheoA: number,
  rheoB: number,
  rheoC: number,
  rheoD: number,
  concentration: number,
  pipeInnerDiameter: number,
  velocity: number,
) {
  const yieldStress = calculateYieldfromConcentrationByMass(concentration * 100, rheoA, rheoB);
  const viscosity = calculateViscosityfromConcentrationByMass(concentration * 100, rheoC, rheoD);
  return frictionFromRheology(yieldStress, viscosity, pipeInnerDiameter, velocity) ?? 0;
}

// kPa/m. Density in kg/m3, viscosity in Pa.s. The viscosity has always been water at 25 degrees; it is
// a parameter so the flush solver can run the same correlation at 20 degrees without changing any
// existing number.
export function homogeneousNewtonian(
  pipeInnerDiameter: number,
  velocity: number,
  slurryDensity: number,
  viscosity: number = WATER_VISCOSITY_25C,
) {
  const fluidConsistencyIndex = viscosity;
  const Re = (slurryDensity * velocity * pipeInnerDiameter) / fluidConsistencyIndex;
  const wallRoughness = 0.000050; // typical wall roughness for lightly worn steel pipe
  const A = (2.457 * Math.log((((7 / Re) ** 0.9) + (0.27 * (wallRoughness / pipeInnerDiameter))) ** -1)) ** 16;
  const B = (37530 / Re) ** 16;
  const frictionFactor = 2 * (((8 / Re) ** 12) + ((A + B) ** -1.5)) ** 0.0833; // Churchill(1977) correlation

  const pGrad = (2 * slurryDensity * frictionFactor * (velocity ** 2)) / pipeInnerDiameter;
  return pGrad / 1000;
}

export function calculateFriction(
  rheoA: number,
  rheoB: number,
  rheoC: number,
  rheoD: number,
  concentration: number,
  pipeInnerDiameter: number,
  velocity: number,
  slurryDensity: number,
  frictionLossFactorOfSafety?: number,
) {
  const safetyFactor = frictionLossFactorOfSafety || 1;
  if (rheoA === 0) {
    return homogeneousNewtonian(pipeInnerDiameter, velocity, slurryDensity) * safetyFactor;
  }
  return homogeneousNonNewtonian(rheoA, rheoB, rheoC, rheoD, concentration, pipeInnerDiameter, velocity) * safetyFactor;
}

export function calculateFrictionForRoute(
  model: Model,
  recipeStateOverride?: RecipeState,
  routeOverride?: PCRoute,
  rheologyOverride?: RheologyDataDto,
): PCRoute {
  const pipes = Object.values(model.pipes);
  const selectedRoute = routeOverride ?? returnShortestRoute(model.routes) as PCRoute;
  const recipeState = recipeStateOverride ?? model.recipeState;
  if (recipeState === undefined) {
    return selectedRoute;
  }
  const { wetFlowRate, totalDryConcentrationByMass, wetDensity } = recipeState as RecipeState;

  if (!wetFlowRate || !totalDryConcentrationByMass || !wetDensity) {
    return selectedRoute;
  }
  const rheology = rheologyOverride ?? resolveRheology(model, recipeState);
  if (!rheology) {
    return selectedRoute;
  }
  pipes.forEach((pipe) => {
    const friction = calculateFriction(
      rheology.yieldStressCoefficients.coefficient1,
      rheology.yieldStressCoefficients.coefficient2,
      rheology.viscosityCoefficients.coefficient1,
      rheology.viscosityCoefficients.coefficient2,
      totalDryConcentrationByMass,
      pipe.insideDiameter,
      pipe.getVelocity(wetFlowRate),
      wetDensity,
      rheology.frictionLossFactorOfSafety,
    );
    pipe.setFriction(friction);
  });

  selectedRoute.updateRoutePointData();
  selectedRoute.updateRoutePointPressure(wetDensity);
  selectedRoute.colourRouteByPressure();

  return selectedRoute;
}
